import { ShieldCheck, Sparkles, CheckCircle2, FileText, AlertTriangle } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { AskBox } from "@/components/home/AskBox";

const SampleCard = () => {
  const { t, lang } = useLang();
  const rows = lang === "hi"
    ? [[CheckCircle2, "समस्या की समझ"], [Sparkles, "सुझाया गया समाधान"], [CheckCircle2, "तुरंत करें — चेकलिस्ट"], [FileText, "ज़रूरी दस्तावेज़"], [AlertTriangle, "पुष्टि ज़रूरी"]]
    : [[CheckCircle2, "Problem understanding"], [Sparkles, "Recommended solution"], [CheckCircle2, "Immediate next steps"], [FileText, "Required documents"], [AlertTriangle, "Needs confirmation"]];
  return (
    <div className="absolute -bottom-8 -left-10 w-64 rounded-2xl border border-white/50 bg-white/85 p-5 shadow-lift backdrop-blur-xl animate-fade-up" style={{ animationDelay: ".4s" }} data-testid="hero-sample-card">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-brand">{t("hero.sampleLabel")}</p>
      <ul className="space-y-2.5">
        {rows.map(([Icon, label], i) => (
          <li key={label} className="flex items-center gap-2.5 text-sm text-navy">
            <Icon className={`h-4 w-4 ${i === 4 ? "text-amber-500" : "text-brand"}`} />{label}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Hero = ({ guestLimit }) => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden bg-navy text-white" data-testid="hero-section">
      <div className="grid-bg absolute inset-0" aria-hidden="true" />
      <div className="absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-brand/40 blur-[120px]" aria-hidden="true" />
      <div className="container-z relative grid gap-12 pb-20 pt-14 lg:grid-cols-[1.25fr_1fr] lg:pb-28 lg:pt-20">
        <div className="animate-fade-up">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-cyan">
            <Sparkles className="h-3.5 w-3.5" /> {t("hero.eyebrow")}
          </p>
          <h1 className="font-hi mt-6 text-4xl font-bold leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {t("hero.title1")} <br /><span className="text-cyan">{t("hero.title2")}</span>
          </h1>
          <p className="mt-4 text-base font-medium text-white/90 md:text-lg">{t("hero.tagline")}</p>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">{t("hero.sub")}</p>
          <div className="mt-8 max-w-2xl"><AskBox /></div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/70">
            <span className="inline-flex items-center gap-1.5" data-testid="hero-guest-limit"><CheckCircle2 className="h-4 w-4 text-cyan" />{t("hero.free", { n: guestLimit })}</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-cyan" />{t("hero.independent")}</span>
          </div>
        </div>
        <div className="relative hidden lg:block">
          <div className="relative ml-auto h-[520px] w-[400px] overflow-hidden rounded-[2rem] ring-1 ring-white/10">
            <img
              src="https://images.unsplash.com/photo-1604177091072-b7b677a077f6?crop=entropy&cs=srgb&fm=jpg&q=70&w=800"
              alt="Student with laptop on campus"
              className="h-full w-full object-cover"
              fetchPriority="high"
              width="400" height="520"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent" />
          </div>
          <SampleCard />
        </div>
      </div>
    </section>
  );
};
